import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Grid, PageWrapper } from "components/Containers";
import { useAuth, useOrganization, useParamFunctions } from "hooks";
import { Banner, BannerShowcaseSignup } from "components/Banner";
import { css } from "@emotion/react";
import { MISSION_ROLES_COLUMNS } from "constants/index";
import { RoleAddButton, RolesFilters, RolesList, KEYS } from "components/Missions/Roles";
import {
  AIRoleBuilderButton,
  AIRoleBuilderTasks,
  AIRoleBuilderTasksProvider,
} from "components/Missions/Roles/AIRoleBuilder";
import PageTitle from "components/PageTitle";
import { PrimaryLink } from "components/Links";

/**
 * Client Mission Board
 */
const ClientMissionBoard = () => {
  const { paramParse, setParams } = useParamFunctions();
  const parsedFilters = paramParse(KEYS.filters);
  const {
    canViewUnpublishedRoles,
    canViewUnpublishedMissions,
    isShowcase,
    isClient,
    canRequestRole,
    canBuildRoleWithAi,
    organization,
  } = useAuth();
  const { data, loading: organizationLoading } = useOrganization({
    id: canBuildRoleWithAi ? organization?.slug : undefined,
  });
  const { id: defaultMissionId } = data?.defaultMission || {};
  // Set initial view as List view for clients, Card view for showcase
  const [isCardView, setIsCardView] = useState(false);
  const publishFilter = isClient
    ? { publishedMissions: !canViewUnpublishedMissions }
    : { published: !canViewUnpublishedRoles };
  const missionFilter = { includeDefaultMissions: canBuildRoleWithAi || canRequestRole };

  const handleViewChange = () => {
    setIsCardView(!isCardView);
    setParams({ [KEYS.filters]: { keyword: "" } });
  };

  const emptyMessageProps = {
    description: "Missions.Roles.RolesList.ClientEmpty",
  };

  const board = (
    <PageWrapper>
      <BannerShowcaseSignup
        css={css`
          margin: 4rem 0 1rem 0;
        `}
      />
      {isShowcase && (
        <Banner keyName="showClientMissionBoardBanner">
          <FormattedMessage
            id="Missions.Board.Banner.Title1"
            values={{
              intro: (
                <b>
                  <FormattedMessage id="Missions.Board.Banner.Title2" />
                </b>
              ),
            }}
          />
        </Banner>
      )}
      <Grid>
        <Grid.col start={1} end={13}>
          <PageTitle
            title={<FormattedMessage id="Client.MissionBoard.H1" />}
            actionBar={
              isClient && (
                <>
                  {canBuildRoleWithAi && (
                    <AIRoleBuilderButton
                      companyName={organization?.name}
                      companyUrl={organization?.url}
                      missionId={defaultMissionId}
                      hideDescriptionField
                    />
                  )}
                  {canRequestRole && <RoleAddButton />}
                  <PrimaryLink onClick={handleViewChange} withMoreIcon>
                    <FormattedMessage id={`Client.MissionBoard.SwitchViewButton.${isCardView ? "Card" : "List"}View`} />
                  </PrimaryLink>
                </>
              )
            }
          />
        </Grid.col>
        {canBuildRoleWithAi && (
          <Grid.col start={1} end={13}>
            <AIRoleBuilderTasks missionId={defaultMissionId} />
          </Grid.col>
        )}
        {!isCardView && (
          <Grid.col start={1} end={13}>
            <RolesFilters hideTalentOwnerFilter hideSalesOwnerFilter />
          </Grid.col>
        )}
        <Grid.col start={1} end={13}>
          <RolesList
            isCardView={isCardView}
            filters={{ ...parsedFilters, ...publishFilter, ...missionFilter }}
            columns={COLUMNS}
            showAdvancedOptions={false}
            selectable={false}
            messageProps={emptyMessageProps}
            emptyMessage={emptyMessageProps}
          />
        </Grid.col>
      </Grid>
    </PageWrapper>
  );

  // render the board without the AI Role Builder context if the user cannot
  // build roles with AI, to avoid unnecessary GraphQL queries and context overhead.
  if (!canBuildRoleWithAi) return board;

  return (
    <AIRoleBuilderTasksProvider missionId={defaultMissionId} loading={organizationLoading}>
      {board}
    </AIRoleBuilderTasksProvider>
  );
};

const COLUMNS = [
  MISSION_ROLES_COLUMNS.role,
  MISSION_ROLES_COLUMNS.skillsAndDisciplines,
  MISSION_ROLES_COLUMNS.salesOwner,
  MISSION_ROLES_COLUMNS.status,
  MISSION_ROLES_COLUMNS.pipeline,
  MISSION_ROLES_COLUMNS.viewButton,
];

export default ClientMissionBoard;
